import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Product, CartItem } from '../services/storeService';

interface CartContextType {
    items: CartItem[];
    itemCount: number;
    totalAmount: number;
    addItem: (product: Product, quantity?: number, isSubscription?: boolean, selectedInterval?: any) => void;
    updateQuantity: (productId: number, quantity: number, isSubscription?: boolean, selectedInterval?: any) => void;
    removeItem: (productId: number, isSubscription: boolean, selectedInterval?: any) => void;
    clearCart: () => void;
    getItem: (productId: number) => CartItem | undefined;
    updateMembershipStatus: (memberships: Array<{ productId: number; hasActiveMembership: boolean }>) => void;
    updateSubscriptionInterval: (productId: number, selectedInterval: any) => void;
}

const WebinarBundleCartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
    children: ReactNode;
    skipSessionStorage?: boolean; // If true, don't load from sessionStorage on init
}

export const WebinarBundleCartProvider: React.FC<CartProviderProps> = ({ children, skipSessionStorage = false }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const initializationRef = useRef(false);

    // Load cart from sessionStorage on mount - only once
    useEffect(() => {
        if (initializationRef.current) return;
        initializationRef.current = true;

        try {
            // Skip loading from sessionStorage if skipSessionStorage prop is true
            if (!skipSessionStorage) {
                // Use different key for webinar bundle cart
                const savedCart = sessionStorage.getItem('alyze-webinar-bundle-cart');
                if (savedCart) {
                    const parsedCart = JSON.parse(savedCart);
                    setItems(parsedCart);
                }
            } else {
                // Clear sessionStorage if we're skipping it to ensure fresh start
                sessionStorage.removeItem('alyze-webinar-bundle-cart');
            }
        } catch (error) {
            console.error('Error loading cart from sessionStorage:', error);
        } finally {
            setIsInitialized(true);
        }
    }, [skipSessionStorage]);

    // Save cart to sessionStorage whenever items change (but only after initialization)
    useEffect(() => {
        if (!isInitialized) return;

        try {
            // Use different key for webinar bundle cart
            sessionStorage.setItem('alyze-webinar-bundle-cart', JSON.stringify(items));
        } catch (error) {
            console.error('Error saving cart to sessionStorage:', error);
        }
    }, [items, isInitialized]);

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const addItem = (product: Product, quantity: number = 1, isSubscription: boolean = false, selectedInterval?: any) => {
        console.log('WebinarBundleCartContext - addItem called with:', { product, quantity, isSubscription });
        
        setItems(prevItems => {
            console.log('WebinarBundleCartContext - prevItems:', prevItems);
            
            // For subscription items, check product ID, subscription status, AND interval
            // For one-time items, only check product ID and subscription status
            const existingItem = prevItems.find(item => {
                if (item.productId !== product.productId || item.isSubscription !== isSubscription) {
                    return false;
                }
                
                // If it's a subscription item, also check if intervals match
                if (isSubscription && selectedInterval) {
                    return item.selectedInterval?.billingInterval === selectedInterval.billingInterval &&
                           item.selectedInterval?.billingPeriod === selectedInterval.billingPeriod;
                }
                
                // For one-time purchases, just matching productId and isSubscription is enough
                return !isSubscription;
            });

            let newItems: CartItem[];
            if (existingItem) {
                newItems = prevItems.map(item => {
                    // Find the exact matching item
                    const isExactMatch = item.productId === product.productId && 
                                       item.isSubscription === isSubscription &&
                                       (!isSubscription || 
                                        (item.selectedInterval?.billingInterval === selectedInterval?.billingInterval &&
                                         item.selectedInterval?.billingPeriod === selectedInterval?.billingPeriod));
                    
                    if (isExactMatch) {
                        return { 
                            ...item, 
                            quantity: item.quantity + quantity,
                            selectedInterval: isSubscription ? selectedInterval : undefined
                        };
                    }
                    return item;
                });
            } else {
                // Use the currentPrice if available, otherwise use membership/retail price
                const basePrice = (product as any).currentPrice || 
                                 (product.hasMembership ? product.membershipPrice : product.retailPrice);

                const newItem: CartItem = {
                    productId: product.productId,
                    quantity,
                    price: basePrice,
                    product: {
                        ...product,
                        productType: product.productType || 'simple'
                    },
                    isSubscription,
                    selectedInterval: isSubscription ? selectedInterval : undefined,
                };
                newItems = [...prevItems, newItem];
            }

            console.log('WebinarBundleCartContext - newItems:', newItems);
            return newItems;
        });
    };

    const updateQuantity = (productId: number, quantity: number, isSubscription: boolean = false, selectedInterval?: any) => {
        if (quantity <= 0) {
            removeItem(productId, isSubscription, selectedInterval);
            return;
        }

        setItems(prevItems =>
            prevItems.map(item => {
                // Match based on productId, subscription status, and interval (if applicable)
                const isMatch = item.productId === productId && 
                              item.isSubscription === isSubscription &&
                              (!isSubscription || 
                               (item.selectedInterval?.billingInterval === selectedInterval?.billingInterval &&
                                item.selectedInterval?.billingPeriod === selectedInterval?.billingPeriod));
                
                return isMatch ? { ...item, quantity } : item;
            })
        );
    };

    const removeItem = (productId: number, isSubscription: boolean = false, selectedInterval?: any) => {
        setItems(prevItems =>
            prevItems.filter(item => {
                // Remove based on productId, subscription status, and interval (if applicable)
                const isMatch = item.productId === productId && 
                              item.isSubscription === isSubscription &&
                              (!isSubscription || 
                               (item.selectedInterval?.billingInterval === selectedInterval?.billingInterval &&
                                item.selectedInterval?.billingPeriod === selectedInterval?.billingPeriod));
                
                return !isMatch;
            })
        );
    };

    const clearCart = () => {
        setItems([]);
    };

    const getItem = (productId: number): CartItem | undefined => {
        return items.find(item => item.productId === productId);
    };

    const updateMembershipStatus = (memberships: Array<{ productId: number; hasActiveMembership: boolean }>) => {
        setItems(prevItems =>
            prevItems.map(item => {
                const membership = memberships.find(m => m.productId === item.productId);
                if (membership) {
                    return { ...item, hasActiveMembership: membership.hasActiveMembership };
                }
                return item;
            })
        );
    };

    const updateSubscriptionInterval = (productId: number, selectedInterval: any) => {
        setItems(prevItems =>
            prevItems.map(item =>
                item.productId === productId && item.isSubscription
                    ? { ...item, selectedInterval }
                    : item
            )
        );
    };

    return (
        <WebinarBundleCartContext.Provider
            value={{
                items,
                itemCount,
                totalAmount,
                addItem,
                updateQuantity,
                removeItem,
                clearCart,
                getItem,
                updateMembershipStatus,
                updateSubscriptionInterval,
            }}
        >
            {children}
        </WebinarBundleCartContext.Provider>
    );
};

export const useWebinarBundleCart = () => {
    const context = useContext(WebinarBundleCartContext);
    if (context === undefined) {
        throw new Error('useWebinarBundleCart must be used within a WebinarBundleCartProvider');
    }
    return context;
};
